import React, { useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const initial = {
    name: "",
    email: "",
    phone: "",
    district: "",
    subject: "",
    message: "",
};

export default function Contact() {
    const { t } = useLang();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await apiClient.post("/enquiries", form);
            toast.success(t.contact.success);
            setForm(initial);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="bg-[#1A365D] text-white pt-20 pb-16 md:pt-28 md:pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="small-caps text-[#F5B76E] mb-4">{t.contact.eyebrow}</div>
                    <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl mb-6">
                        {t.contact.title}
                    </h1>
                    <p className="max-w-2xl text-gray-300 leading-relaxed">
                        {t.contact.subtitle}
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-7">
                        <form
                            onSubmit={submit}
                            className="space-y-6 bg-[#F7FAFC] p-8 md:p-10 rounded-lg border border-gray-200"
                            data-testid="enquiry-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <Field label={t.contact.name}>
                                    <Input
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={change("name")}
                                        data-testid="enquiry-name"
                                    />
                                </Field>
                                <Field label={t.contact.email}>
                                    <Input
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={change("email")}
                                        data-testid="enquiry-email"
                                    />
                                </Field>
                                <Field label={t.contact.phone}>
                                    <Input
                                        required
                                        minLength={6}
                                        value={form.phone}
                                        onChange={change("phone")}
                                        data-testid="enquiry-phone"
                                    />
                                </Field>
                                <Field label={t.contact.district}>
                                    <Input
                                        required
                                        value={form.district}
                                        onChange={change("district")}
                                        data-testid="enquiry-district"
                                    />
                                </Field>
                            </div>
                            <Field label={t.contact.subject}>
                                <Input
                                    required
                                    value={form.subject}
                                    onChange={change("subject")}
                                    data-testid="enquiry-subject"
                                />
                            </Field>
                            <Field label={t.contact.message}>
                                <Textarea
                                    required
                                    minLength={5}
                                    rows={5}
                                    value={form.message}
                                    onChange={change("message")}
                                    data-testid="enquiry-message"
                                />
                            </Field>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="bg-[#DD6B20] hover:bg-[#C05621] text-white h-12 px-8"
                                data-testid="enquiry-submit"
                            >
                                {submitting ? t.contact.submitting : t.contact.submit}
                            </Button>
                        </form>
                    </div>

                    <aside className="md:col-span-5 space-y-4">
                        <div className="bg-[#1A365D] text-white p-8 rounded-lg">
                            <div className="small-caps text-[#F5B76E] mb-3">
                                {t.contact.hqTitle}
                            </div>
                            <h3 className="font-serif text-2xl font-semibold mb-6">
                                {t.contact.hqTitle}
                            </h3>
                            <ul className="space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <MapPin size={18} className="mt-0.5 text-[#F5B76E]" />
                                    <span className="text-gray-200 leading-relaxed">
                                        {t.contact.hqAddress}
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone size={18} className="mt-0.5 text-[#F5B76E]" />
                                    <span className="text-gray-200">{t.contact.hqPhone}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Mail size={18} className="mt-0.5 text-[#F5B76E]" />
                                    <span className="text-gray-200">{t.contact.hqEmail}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Clock size={18} className="mt-0.5 text-[#F5B76E]" />
                                    <span className="text-gray-200">{t.contact.hours}</span>
                                </li>
                            </ul>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest font-semibold text-gray-600 mb-2 inline-block">
                {label}
            </span>
            {children}
        </label>
    );
}
